/**
 * The equity transform — pure, testable, and the place the three chronic
 * equity bugs are designed out on the client side.
 *
 * The server already emits a TYPED, gap-explicit series (`/ledger/equity`).
 * This module's whole job is to turn it into uPlot arrays **without adding a
 * single value the server did not send**. Concretely:
 *
 *  * **No interpolation across a declared gap** (§6.8 rule 7). A gap becomes
 *    an explicit all-null slot inside the gap interval, which uPlot draws as
 *    a break. Two adjacent real points with a declared gap between them
 *    would otherwise be joined by a straight line — a drawn lie.
 *  * **MARK is never joined to the realized line** (C3 / §S5). The `balance=`
 *    log line is equity INCLUDING unrealized at 10–20×; it moves tens of
 *    percent inside one open trade. It is a separate series, off by default,
 *    and it is never an anchor.
 *  * **A reading inside an open span is a separate dimmer lane**, never
 *    dropped. Dropping points is what annihilated 3,780 real readings (C1).
 *  * **Non-finite readings are rejected with a visible counter**, never
 *    coerced to 0 (§6.8 rule 9). A literal NaN 500s the old endpoint.
 *
 * The one synthetic quantity in this file is `DUP_EPSILON_S`: uPlot requires
 * strictly-ascending x values, and a capital flow deliberately emits TWO
 * points at the same instant so the curve STEPS. The second is nudged by one
 * millisecond on the x axis only. No y value is ever synthesised.
 */

import type { EquityPayload, EquityPoint } from "./api";

/** one millisecond, in the chart's x unit (seconds). x-axis only. */
export const DUP_EPSILON_S = 0.001;

export type Lane = "realized" | "unrealized" | "mark";

export interface PlotData {
  /** unix seconds, strictly ascending */
  xs: number[];
  realized: Array<number | null>;
  /** measured readings taken while an order was open — dimmer, points only */
  unrealized: Array<number | null>;
  /** the MARK overlay: equity incl. unrealized. Off by default in the chart. */
  mark: Array<number | null>;
  /** indices in `xs` that are synthetic gap breaks (all lanes null there) */
  breakIndices: number[];
  /** x positions of capital flows, for step markers */
  flows: Array<{ x: number; amount: number; kind: string }>;
  /** clamped unterminated opens — drawn as fault markers, never extended */
  unterminated: Array<{ open: number; clampedTo: number }>;
  /** readings the client refused because they were not finite */
  rejectedNonFinite: number;
  /** the aggregate bounded-unknown band, verbatim from the server */
  band: EquityPayload["band"];
}

const REALIZED_SRC = new Set(["daily", "synth", "flow"]);

export function laneOf(p: EquityPoint): Lane {
  // `src === 'log'` is the MARK population. It is decided by SOURCE, not by
  // kind, because a log reading taken while flat is still a mark-to-market
  // number on a leveraged account and still must not anchor the realized
  // back-walk (C3).
  if (p.src === "log") return "mark";
  if (p.kind === "measured_unrealized") return "unrealized";
  return REALIZED_SRC.has(p.src) ? "realized" : "unrealized";
}

function secs(iso: string): number | null {
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t / 1000 : null;
}

interface Slot {
  x: number;
  realized: number | null;
  unrealized: number | null;
  mark: number | null;
  isBreak: boolean;
}

export function toPlotData(payload: EquityPayload | null | undefined): PlotData {
  const empty: PlotData = {
    xs: [],
    realized: [],
    unrealized: [],
    mark: [],
    breakIndices: [],
    flows: [],
    unterminated: [],
    rejectedNonFinite: 0,
    band: payload?.band ?? { low: 0, high: 0, n_unsettled: 0, basis: "" },
  };
  if (!payload || !Array.isArray(payload.points)) return empty;

  let rejected = 0;
  const raw: Array<{ x: number; lane: Lane; y: number }> = [];
  for (const p of payload.points) {
    const x = secs(p.ts);
    if (x === null) {
      rejected += 1;
      continue;
    }
    if (typeof p.balance !== "number" || !Number.isFinite(p.balance)) {
      // Never imputed to zero. Counted, and the counter is rendered.
      rejected += 1;
      continue;
    }
    raw.push({ x, lane: laneOf(p), y: p.balance });
  }
  raw.sort((a, b) => a.x - b.x);

  // --- one slot per distinct x; duplicates step by one millisecond -------
  const slots: Slot[] = [];
  let lastX = Number.NEGATIVE_INFINITY;
  for (const r of raw) {
    let x = r.x;
    if (x <= lastX) x = lastX + DUP_EPSILON_S;
    slots.push({
      x,
      realized: r.lane === "realized" ? r.y : null,
      unrealized: r.lane === "unrealized" ? r.y : null,
      mark: r.lane === "mark" ? r.y : null,
      isBreak: false,
    });
    lastX = x;
  }

  // --- declared gaps become explicit all-null breaks ---------------------
  // A gap is DECLARED by the server with a duration. The client's only job
  // is to make sure the renderer cannot draw through it.
  for (const g of payload.gaps ?? []) {
    const from = secs(g.from);
    const to = secs(g.to);
    if (from === null || to === null || !(to > from)) continue;
    const mid = from + (to - from) / 2;
    if (slots.some((s) => Math.abs(s.x - mid) < DUP_EPSILON_S)) continue;
    slots.push({ x: mid, realized: null, unrealized: null, mark: null, isBreak: true });
  }
  slots.sort((a, b) => a.x - b.x);

  const xs: number[] = [];
  const realized: Array<number | null> = [];
  const unrealized: Array<number | null> = [];
  const mark: Array<number | null> = [];
  const breakIndices: number[] = [];
  slots.forEach((s, i) => {
    xs.push(s.x);
    realized.push(s.realized);
    unrealized.push(s.unrealized);
    mark.push(s.mark);
    if (s.isBreak) breakIndices.push(i);
  });

  const flows = (payload.flows ?? [])
    .map((f) => {
      const x = f.ts ? secs(f.ts) : null;
      return x !== null && typeof f.amount_usd === "number" && Number.isFinite(f.amount_usd)
        ? { x, amount: f.amount_usd, kind: f.kind }
        : null;
    })
    .filter((f): f is { x: number; amount: number; kind: string } => f !== null);

  const unterminated = (payload.unterminated_spans ?? [])
    .map((u) => {
      const o = u.open ? secs(u.open) : null;
      const c = u.clamped_to ? secs(u.clamped_to) : null;
      return o !== null && c !== null ? { open: o, clampedTo: c } : null;
    })
    .filter((u): u is { open: number; clampedTo: number } => u !== null);

  return {
    xs,
    realized,
    unrealized,
    mark,
    breakIndices,
    flows,
    unterminated,
    rejectedNonFinite: rejected,
    band: payload.band ?? empty.band,
  };
}

/** The set of x values the server actually sent, for the "nothing was
 *  invented" assertion. Exported because the regression test uses it and a
 *  test that reimplements the rule proves nothing. */
export function sourceTimestamps(payload: EquityPayload): number[] {
  return payload.points.map((p) => secs(p.ts)).filter((x): x is number => x !== null);
}
